import React, { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Settings2, ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

// Import the icon resolver utilities
import {
  Radar, Satellite, Compass, Router, Server, Radio, Antenna, Signal,
  MapPin, Navigation, Globe, Shield, ShieldAlert, ShieldCheck,
  AlertTriangle, Eye, Camera, Video, Lightbulb, Battery,
  Zap, Phone, Headphones, Mic, Wifi, Cloud, Plane,
  Cpu, Monitor, HardDrive, Gauge, Crosshair, Target, Lock,
  Users, Armchair, Waves, Anchor, Droplets, Wrench,
  Star, Package, Heart, Activity, Stethoscope, Ambulance,
  Siren, Thermometer, Snowflake, Sun, Wind, Flame,
  Volume2, Moon, Focus, Scan,
  Route, MapPinned, Search, Binoculars,
  Power, Plug, CircuitBoard, Microchip,
  Cog, Hammer, Flashlight, Sparkles, Navigation2, Sliders
} from 'lucide-react';

interface EquipmentBadgesProps {
  equipment?: string[];
  maxShow?: number;
  className?: string;
  /** Show full equipment names without truncation */
  fullNames?: boolean;
  /** Compact mode for smaller cards */
  compact?: boolean;
  /** Mobile mode - horizontal scroll instead of grid */
  mobileMode?: boolean;
}

// Keyword to icon mapping for equipment
const keywordIconMap: Record<string, LucideIcon> = {
  gps: Satellite,
  navigation: Navigation,
  nav: Compass,
  radar: Radar,
  transponder: Antenna,
  radio: Radio,
  intercom: Headphones,
  headset: Headphones,
  communication: Phone,
  comm: Signal,
  autopilot: Cpu,
  avionics: Monitor,
  display: Monitor,
  glass: Monitor,
  cockpit: Gauge,
  instrument: Gauge,
  camera: Camera,
  flir: Eye,
  thermal: Thermometer,
  searchlight: Flashlight,
  spotlight: Lightbulb,
  light: Lightbulb,
  night: Moon,
  nvg: Eye,
  vision: Eye,
  hoist: Anchor,
  winch: Anchor,
  cargo: Package,
  hook: Anchor,
  float: Waves,
  emergency: Siren,
  ems: Ambulance,
  medical: Stethoscope,
  stretcher: Heart,
  litter: Heart,
  police: ShieldAlert,
  law: Shield,
  enforcement: ShieldCheck,
  rescue: Target,
  sar: Search,
  search: Binoculars,
  fire: Flame,
  bucket: Droplets,
  bambi: Droplets,
  tank: Droplets,
  spray: Wind,
  agricultural: Plane,
  ag: Plane,
  utility: Wrench,
  vip: Star,
  executive: Armchair,
  luxury: Sparkles,
  passenger: Users,
  seating: Armchair,
  seat: Armchair,
  interior: Armchair,
  leather: Armchair,
  air: Wind,
  conditioning: Snowflake,
  heater: Sun,
  heat: Flame,
  ice: Snowflake,
  weather: Cloud,
  wire: AlertTriangle,
  strike: AlertTriangle,
  protection: Shield,
  armor: ShieldCheck,
  battery: Battery,
  power: Power,
  electrical: Zap,
  generator: Plug,
  aux: CircuitBoard,
  auxiliary: CircuitBoard,
  fuel: Gauge,
  extended: Route,
  filter: Wind,
  engine: Cog,
  rotor: Navigation2,
  blade: Sliders,
  maintenance: Hammer,
  tracking: MapPinned,
  locator: MapPin,
  beacon: Signal,
  elt: Siren,
  wifi: Wifi,
  satellite: Satellite,
  data: Server,
  computer: Microchip,
  processor: Cpu,
  system: CircuitBoard,
  audio: Volume2,
  speaker: Volume2,
  mic: Mic,
  recorder: HardDrive,
  video: Video,
  dvr: HardDrive,
  survey: Target,
  mapping: Globe,
  lidar: Scan,
  sensor: Focus,
  mission: Crosshair,
  tactical: Target,
  military: Shield,
  combat: ShieldAlert,
  defense: ShieldCheck,
  security: Lock,
  monitor: Activity,
  health: Heart,
  hums: Activity
};

// Get icon for equipment name based on keywords
const getEquipmentIconComponent = (name: string): LucideIcon => {
  const lowerName = name.toLowerCase();
  
  for (const [keyword, icon] of Object.entries(keywordIconMap)) {
    if (lowerName.includes(keyword)) {
      return icon;
    }
  }
  
  return Cog; // Default fallback
};

// Resolve unique icons for equipment list
const resolveEquipmentIcons = (equipment: string[]): Map<string, LucideIcon> => {
  const usedIcons = new Set<LucideIcon>();
  const result = new Map<string, LucideIcon>();
  
  // All available icons for fallback
  const allIcons: LucideIcon[] = [
    Radar, Satellite, Compass, Router, Server, Radio, Antenna, Signal,
    MapPin, Navigation, Globe, Shield, ShieldAlert, ShieldCheck,
    Eye, Camera, Video, Lightbulb, Battery, Zap, Phone, Headphones,
    Mic, Wifi, Cloud, Plane, Cpu, Monitor, HardDrive, Gauge, Crosshair,
    Target, Lock, Users, Armchair, Waves, Anchor, Droplets, Wrench,
    Star, Package, Heart, Activity, Stethoscope, Siren, Thermometer,
    Snowflake, Sun, Wind, Flame, Volume2, Moon, Focus, Scan, Route,
    MapPinned, Search, Binoculars, Power, Plug, CircuitBoard, Microchip,
    Cog, Hammer, Flashlight, Sparkles, Navigation2, Sliders
  ];
  
  for (const item of equipment) {
    let icon = getEquipmentIconComponent(item);
    
    // If icon already used, find an alternative
    if (usedIcons.has(icon)) {
      const hash = item.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      for (let i = 0; i < allIcons.length; i++) {
        const candidate = allIcons[(hash + i) % allIcons.length];
        if (!usedIcons.has(candidate)) {
          icon = candidate;
          break;
        }
      }
    }
    
    usedIcons.add(icon);
    result.set(item, icon);
  }
  
  return result;
};

export const EquipmentBadges: React.FC<EquipmentBadgesProps> = ({
  equipment,
  maxShow = 4,
  className,
  fullNames = true,
  compact = false,
  mobileMode = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useIsMobile();
  const useMobileLayout = mobileMode || isMobile;
  
  // Resolve unique icons for all equipment
  const iconsMap = useMemo(() => {
    if (!equipment || equipment.length === 0) return new Map<string, LucideIcon>();
    return resolveEquipmentIcons(equipment);
  }, [equipment]);

  if (!equipment || equipment.length === 0) return null;

  const visibleEquipment = isExpanded ? equipment : equipment.slice(0, maxShow);
  const remainingCount = equipment.length - maxShow;
  const hasMore = remainingCount > 0;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Header with expand/collapse toggle */}
      <div 
        className={cn(
          "flex items-center justify-between",
          hasMore && "cursor-pointer hover:opacity-80 transition-opacity"
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (hasMore) setIsExpanded(!isExpanded);
        }}
      >
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Settings2 className="w-3 h-3 text-slate-500" />
          <span className="uppercase tracking-wider font-semibold">Equipment</span>
          <span className="text-muted-foreground/60">({equipment.length})</span>
        </div>
        {hasMore && (
          <ChevronDown 
            className={cn(
              "w-4 h-4 text-muted-foreground transition-transform duration-200",
              isExpanded && "rotate-180"
            )} 
          />
        )}
      </div>
      
      {/* Equipment badges - vertical list on mobile when expanded, horizontal scroll when collapsed, grid on desktop */}
      {useMobileLayout ? (
        isExpanded ? (
          // Mobile expanded: vertical stacked list
          <div className="flex flex-col gap-1.5">
            {equipment.map((item, index) => {
              const IconComponent = iconsMap.get(item) || Cog;
              return (
                <Badge 
                  key={index}
                  variant="outline"
                  className="text-[10px] px-2 py-1.5 h-auto justify-start bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                >
                  <IconComponent className="w-3 h-3 text-gray-600 dark:text-gray-400 mr-1.5 flex-shrink-0" />
                  <span className="truncate">{item}</span>
                </Badge>
              );
            })}
          </div>
        ) : (
          // Mobile collapsed: horizontal scroll
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
            {equipment.slice(0, 5).map((item, index) => {
              const IconComponent = iconsMap.get(item) || Cog;
              return (
                <Badge 
                  key={index}
                  variant="outline"
                  className="text-[9px] px-1.5 py-0.5 h-5 flex-shrink-0 whitespace-nowrap bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                >
                  <IconComponent className="w-2.5 h-2.5 text-gray-600 dark:text-gray-400 mr-1" />
                  {item.length > 12 ? `${item.slice(0, 12)}…` : item}
                </Badge>
              );
            })}
            {equipment.length > 5 && (
              <Badge variant="secondary" className="text-[9px] px-1.5 py-0.5 h-5 flex-shrink-0">
                +{equipment.length - 5}
              </Badge>
            )}
          </div>
        )
      ) : (
        <div className="grid grid-cols-2 gap-1.5">
          {visibleEquipment.map((item, index) => {
            const IconComponent = iconsMap.get(item) || Cog;
            return (
              <Badge 
                key={index}
                variant="outline"
                className={cn(
                  "font-normal transition-all duration-200 w-full justify-start",
                  "bg-white dark:bg-slate-800",
                  "text-slate-700 dark:text-slate-300",
                  "border-slate-200 dark:border-slate-700",
                  "hover:bg-slate-50 dark:hover:bg-slate-750 hover:border-slate-300 dark:hover:border-slate-600",
                  "shadow-sm",
                  compact ? "text-[9px] px-1.5 py-0.5 h-auto" : "text-[10px] px-2 py-1 h-auto"
                )}
              >
                <IconComponent className="w-3 h-3 text-gray-600 dark:text-gray-400 mr-1.5 flex-shrink-0" />
                <span className="truncate">
                  {fullNames ? item : (item.length > 18 ? `${item.slice(0, 18)}…` : item)}
                </span>
              </Badge>
            );
          })}
        </div>
      )}
      
      {/* Expand indicator when collapsed */}
      {!isExpanded && hasMore && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(true);
          }}
          className="flex items-center gap-1 text-[10px] text-primary font-medium hover:underline transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          <ChevronDown className="w-3 h-3" />
          Show {remainingCount} more
        </button>
      )}
      
      {/* Collapse indicator when expanded */}
      {isExpanded && hasMore && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(false);
          }}
          className="flex items-center gap-1 text-[10px] text-muted-foreground font-medium hover:text-primary transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          <ChevronDown className="w-3 h-3 rotate-180" />
          Show less
        </button>
      )}
    </div>
  );
};

export default EquipmentBadges;
